import React from 'react';
import { PackageInclusionsSection } from '../PackageInclusionsSection';
import { PackagePricingSection } from '../PackagePricingSection';
import { PackageTripInfoSection } from '../PackageTripInfoSection';
import { BuilderMode, FaqLimits, HighlightLimits, DescriptionLimits } from '../contracts';
import { UsePackageBuilderStateResult } from '../../../hooks/usePackageBuilderState';

export const inclusionOptions = [
  { value: 'flight', label: 'Flights' },
  { value: 'hotel', label: 'Hotels' },
  { value: 'meals', label: 'Meals' },
  { value: 'transfers', label: 'Transfers' },
  { value: 'activities', label: 'Activities' },
  { value: 'visa', label: 'Visa' },
  { value: 'insurance', label: 'Insurance' },
];

export const mealPlanOptions = [
  'None',
  'Breakfast Only',
  'Half Board',
  'Full Board',
  'All Inclusive',
];

export const transferTypeOptions = ['Shared', 'Private'];

export const idealForOptions = [
  'Couple',
  'Bachelor',
  'Adventure Lover',
  'Family',
  'Friends',
  'All',
];

export const hotelCategoryOptions = [
  'Budget',
  '3 Star',
  '4 Star',
  '5 Star',
  'Luxury',
];

export const idealVendorOptions = [
  'Any',
  'MakeMyTrip',
  'TBO',
  'Rayana Travels',
  'Luxury Travels',
  'Other',
];

export const searchTagGroups: { label: string; options: string[] }[] = [
  { label: 'Popular', options: ['Dubai & Abu Dhabi', 'Singapore & Malaysia', 'Thailand', 'Kashmir', 'Goa', 'Kerala', 'Honeymoon'] },
  { label: 'Domestic', options: ['North East', 'Rajasthan', 'Uttarakhand', 'Goa', 'South India', 'Kashmir', 'Shimla & Manali', 'Maharashtra'] },
  { label: 'International', options: ['Middle East', 'South East Asia', 'Nepal', 'Europe', 'Africa', 'Australia'] },
];

export const highlightLimits: HighlightLimits = {
  min: 6,
  max: 8,
  maxChars: 35,
};

export const descriptionLimits: DescriptionLimits = {
  min: 50,
  max: 100,
};

export const faqLimits: FaqLimits = {
  questionMax: 200,
  answerMaxWords: 100,
};

type BuildTripInfoSectionPropsOptions = {
  state: UsePackageBuilderStateResult;
  openHotelModal: (index: number) => void;
  openActivityModal?: (dayIndex: number) => void;
  removeActivity?: (dayIndex: number, activityIndex: number) => void;
  showCustomActivityInclusions?: boolean;
  hideRelatedLinks?: boolean;
  variant?: BuilderMode;
};

export const buildTripInfoSectionProps = ({
  state,
  openHotelModal,
  openActivityModal,
  removeActivity,
  showCustomActivityInclusions = false,
  hideRelatedLinks = false,
  variant = 'package',
}: BuildTripInfoSectionPropsOptions): React.ComponentProps<typeof PackageTripInfoSection> => {
  const { coreState, pricingState, contentState, derivedState, actions } = state;

  return {
    variant,
    tripInfo: coreState.tripInfo,
    handleTripInfoChange: actions.handleTripInfoChange,
    hotelCategory: contentState.hotelCategory,
    rating: pricingState.rating,
    setHotelCategory: actions.setHotelCategory,
    setRating: actions.setRating,
    hotelCategoryOptions,
    descriptionWordCount: derivedState.descriptionWordCount,
    descriptionLimits,
    mealPlan: contentState.mealPlan,
    setMealPlan: actions.setMealPlan,
    mealPlanOptions,
    transferType: contentState.transferType,
    setTransferType: actions.setTransferType,
    transferTypeOptions,
    idealVendor: contentState.idealVendor,
    setIdealVendor: actions.setIdealVendor,
    idealVendorOptions,
    searchTags: contentState.searchTags,
    setSearchTags: actions.setSearchTags,
    searchTagGroups,
    idealFor: contentState.idealFor,
    toggleIdealFor: actions.toggleIdealFor,
    idealForOptions,
    placeDraft: coreState.placeDraft,
    placeDraftValue: coreState.placeDraftValue,
    setPlaceDraft: actions.setPlaceDraft,
    setPlaceDraftValue: actions.setPlaceDraftValue,
    nightsDraft: coreState.nightsDraft,
    setNightsDraft: actions.setNightsDraft,
    addPlace: actions.addPlace,
    setPlaceField: actions.setPlaceField,
    removePlace: actions.removePlace,
    movePlace: actions.movePlace,
    loadCityOptions: actions.loadCityOptions,
    openHotelModal,
    openActivityModal,
    removeActivity,
    highlightLimits,
    highlights: contentState.highlights,
    highlightDraft: contentState.highlightDraft,
    highlightError: contentState.highlightError,
    setHighlightDraft: actions.setHighlightDraft,
    setHighlightError: actions.setHighlightError,
    addHighlight: actions.addHighlight,
    removeHighlight: actions.removeHighlight,
    faqLimits,
    importantTitleDraft: contentState.importantTitleDraft,
    setImportantTitleDraft: actions.setImportantTitleDraft,
    importantTitleCount: derivedState.importantTitleCount,
    importantDescriptionDraft: contentState.importantDescriptionDraft,
    setImportantDescriptionDraft: actions.setImportantDescriptionDraft,
    importantAnswerWordCount: derivedState.importantAnswerWordCount,
    importantInfoError: contentState.importantInfoError,
    setImportantInfoError: actions.setImportantInfoError,
    canAddImportantInfo: derivedState.canAddImportantInfo,
    addImportantInfo: actions.addImportantInfo,
    removeImportantInfo: actions.removeImportantInfo,
    faq: contentState.faq,
    itinerary: coreState.itinerary,
    setItinerary: actions.setItinerary,
    nightsCount: coreState.nightsCount,
    hotelAutoPointsByDay: derivedState.hotelAutoPointsByDay,
    itineraryPointDrafts: coreState.itineraryPointDrafts,
    setItineraryPointDrafts: actions.setItineraryPointDrafts,
    setDayTitle: actions.setDayTitle,
    setDayPoints: actions.setDayPoints,
    addItineraryPoint: actions.addItineraryPoint,
    removeItineraryPoint: actions.removeItineraryPoint,
    ...(showCustomActivityInclusions
      ? {
        customActivityInclusions: contentState.customActivityInclusions,
        setCustomActivityInclusions: actions.setCustomActivityInclusions,
        showCustomActivityInclusions: true,
      }
      : {}),
    setSuppressedHotelPoints: actions.setSuppressedHotelPoints,
    relatedDestinations: contentState.relatedDestinations,
    setRelatedDestinations: actions.setRelatedDestinations,
    relatedPackages: contentState.relatedPackages,
    setRelatedPackages: actions.setRelatedPackages,
    loadPackageOptions: actions.loadPackageOptions,
    hideRelatedLinks,
  };
};

type BuildInclusionsSectionPropsOptions = {
  state: UsePackageBuilderStateResult;
  title?: string;
  hideCustomisableInclusions?: boolean;
  useCustomInclusions?: boolean;
  componentsContent?: React.ReactNode;
  showTransferPricing?: boolean;
};

export const buildInclusionsSectionProps = ({
  state,
  title,
  hideCustomisableInclusions,
  useCustomInclusions,
  componentsContent,
  showTransferPricing,
}: BuildInclusionsSectionPropsOptions): React.ComponentProps<typeof PackageInclusionsSection> => {
  const { coreState, pricingState, contentState, derivedState, actions } = state;

  return {
    title,
    tripInfo: coreState.tripInfo,
    inclusionOptions,
    toggleInclusion: actions.toggleInclusion,
    currencySymbol: derivedState.currencySymbol,
    visaDetails: contentState.visaDetails,
    setVisaDetails: actions.setVisaDetails,
    insuranceDetails: contentState.insuranceDetails,
    setInsuranceDetails: actions.setInsuranceDetails,
    customVisaDetails: contentState.customVisaDetails,
    setCustomVisaDetails: actions.setCustomVisaDetails,
    customInsuranceDetails: contentState.customInsuranceDetails,
    setCustomInsuranceDetails: actions.setCustomInsuranceDetails,
    customFlights: contentState.customFlights,
    removeCustomFlight: actions.removeCustomFlight,
    formatFlightLabel: actions.formatFlightLabel,
    setShowVisaModal: actions.setShowVisaModal,
    setShowInsuranceModal: actions.setShowInsuranceModal,
    setShowCustomVisaModal: actions.setShowCustomVisaModal,
    setShowCustomInsuranceModal: actions.setShowCustomInsuranceModal,
    setShowCustomFlightModal: actions.setShowCustomFlightModal,
    hideCustomisableInclusions,
    useCustomInclusions,
    componentsContent,
    transferPricing: pricingState.transferPricing,
    setTransferPricing: actions.setTransferPricing,
    showTransferPricing,
  };
};

export const buildPricingSectionProps = (
  state: UsePackageBuilderStateResult,
  mode: BuilderMode
): React.ComponentProps<typeof PackagePricingSection> => {
  const { pricingState, derivedState, actions } = state;

  return {
    mode,
    currencySymbol: derivedState.currencySymbol,
    calculatedFlightsTotal: derivedState.calculatedFlightsTotal,
    calculatedHotelsTotal: derivedState.calculatedHotelsTotal,
    calculatedTransfersTotal: derivedState.calculatedTransfersTotal,
    calculatedVisaTotal: derivedState.calculatedVisaTotal,
    calculatedInsuranceTotal: derivedState.calculatedInsuranceTotal,
    calculatedActivitiesTotal: derivedState.calculatedActivitiesTotal,
    calculatedAddonsTotalForPackage: derivedState.calculatedAddonsTotalForPackage,
    priceOverrides: pricingState.priceOverrides,
    setPriceOverride: actions.setPriceOverride,
    inclusionsTotal: derivedState.inclusionsTotal,
    markupType: pricingState.markupType,
    markupValue: pricingState.markupValue,
    markupAmount: derivedState.markupAmount,
    setMarkupType: actions.setMarkupType,
    setMarkupValue: actions.setMarkupValue,
    currency: pricingState.currency,
    setCurrency: actions.setCurrency,
    finalTotal: derivedState.finalTotal,
    displayedTotal: derivedState.displayedTotal,
    quotationTransferTotal: derivedState.quotationTransferCalculation?.total || 0,
    quotationTransferOverride: pricingState.quotationTransferOverride,
    setQuotationTransferOverride: actions.setQuotationTransferOverride,
    quotationFinalOverride: pricingState.quotationFinalOverride,
    setQuotationFinalOverride: actions.setQuotationFinalOverride,
  };
};
